import apiFetch from "@wordpress/api-fetch";
import { createReduxStore, register } from "@wordpress/data";

export const MAGAZINE_BLOCKS_DEVICE_STORE: "magazine-blocks/device" =
	"magazine-blocks/device";

const DEFAULT_TYPES = {
	body: {
		label: "Body",
		key: "body",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	link: {
		label: "Link",
		key: "link",
		color: {
			type: "color",
			value: "#000000",
		},
		hoverColor: {
			type: "color",
			value: "#383CB9",
		},
		value: {},
	},
	paragraph: {
		label: "Paragraph",
		key: "paragraph",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	h1: {
		label: "Heading 1",
		key: "h1",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	h2: {
		label: "Heading 2",
		key: "h2",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	h3: {
		label: "Heading 3",
		key: "h3",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	h4: {
		label: "Heading 4",
		key: "h4",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	h5: {
		label: "Heading 5",
		key: "h5",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	h6: {
		label: "Heading 6",
		key: "h6",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
};

const DEFAULT_COLOR_STYLES = {
	primary: {
		key: "primary",
		label: "Primary Color",
		value: {
			type: "color",
			value: "#f00",
		},
	},
	secondary: {
		key: "secondary",
		label: "Secondary Color",
		value: {
			type: "color",
			value: "#0f0",
		},
	},
	text: {
		key: "text",
		label: "Text Color",
		value: {
			type: "color",
			value: "#000",
		},
	},
	accent: {
		key: "accent",
		label: "Accent Color",
		value: {
			type: "color",
			value: "#00f",
		},
	},
};

const DEFAULT_BUTTON_STYLES = {
	typography: {
		label: "Typography",
		key: "typography",
		color: {
			type: "color",
			value: "#000000",
		},
		value: {},
	},
	textColor: {
		key: "textColor",
		label: "Text Color",
		value: {
			type: "color",
			value: "#f00",
		},
		hoverValue: {
			type: "color",
			value: "#5c5faa",
		},
	},
	background: {
		key: "background",
		label: "Background",
		value: {
			type: "color",
			value: "#f00",
		},
		hoverValue: {
			type: "color",
			value: "#f00",
		},
	},
	boxShadow: {
		key: "boxShadow",
		label: "Box Shadow",
		value: {
			enable: true,
		},
		hoverValue: {},
	},
	borderType: {
		key: "borderType",
		label: "Border Type",
		value: {},
		hoverValue: {},
	},
	borderSize: {
		key: "borderSize",
		label: "Border Size",
		value: {},
		hoverValue: {},
	},
	borderRadius: {
		key: "borderRadius",
		label: "Border Radius",
		value: {},
		hoverValue: {},
	},
	paddingUnlocked: {
		key: "paddingUnlocked",
		label: "Padding (unlock)",
		value: {},
		hoverValue: {},
	},
	paddingLocked: {
		key: "paddingLocked",
		label: "Padding (locked)",
		value: {},
		hoverValue: {},
	},
};

const INITIAL_STATE: {
	colors: {
		default: any;
		custom: any;
	};
	typographies: {
		default: any;
		custom: any;
	};
	buttons: any;
	// @ts-ignore
} = _MAGAZINE_BLOCKS_.globalStyles ?? {
	colors: {
		default: DEFAULT_COLOR_STYLES,
		custom: {},
	},
	typographies: {
		default: DEFAULT_TYPES,
		custom: {},
	},
	buttons: DEFAULT_BUTTON_STYLES,
};

export const stylesStore = createReduxStore("magazine-blocks/styles", {
	reducer(
		state = INITIAL_STATE,
		action: {
			type: string;
			payload: string;
		}
	) {
		switch (action.type) {
			case "SET_DEFAULT_COLOR_STYLES":
				return {
					...state,
					colors: {
						...state.colors,
						default: action.payload,
					},
				};

			case "SET_CUSTOM_COLOR_STYLES":
				return {
					...state,
					colors: {
						...state.colors,
						custom: action.payload,
					},
				};

			case "SET_DEFAULT_TYPE_STYLES":
				return {
					...state,
					typographies: {
						...state.typographies,
						default: action.payload,
					},
				};

			case "SET_CUSTOM_TYPE_STYLES":
				return {
					...state,
					typographies: {
						...state.typographies,
						custom: action.payload,
					},
				};

			case "SET_BUTTON_STYLES":
				return { ...state, buttons: action.payload };

			case "UPDATE_DB":
				apiFetch({
					path: "magazine-blocks/v1/global-styles",
					method: "POST",
					data: state,
				}).catch((e) => {
					console.error(e);
				});

				return state;
		}

		return state;
	},
	actions: {
		setDefaultColorStyles(data: any) {
			return {
				type: "SET_DEFAULT_COLOR_STYLES",
				payload: data,
			};
		},
		setCustomColorStyles(data: any) {
			return {
				type: "SET_CUSTOM_COLOR_STYLES",
				payload: data,
			};
		},
		setDefaultTypeStyles(data: any) {
			return {
				type: "SET_DEFAULT_TYPE_STYLES",
				payload: data,
			};
		},
		setCustomTypeStyles(data: any) {
			return {
				type: "SET_CUSTOM_TYPE_STYLES",
				payload: data,
			};
		},
		setButtonStyles(data: any) {
			return {
				type: "SET_BUTTON_STYLES",
				payload: data,
			};
		},
		updateDB() {
			return {
				type: "UPDATE_DB",
				payload: "",
			};
		},
	},
	selectors: {
		getGlobalStyles(state: typeof INITIAL_STATE) {
			return state;
		},
		getDefaultColorStyles(state: typeof INITIAL_STATE) {
			return state.colors.default;
		},
		getCustomColorStyles(state: typeof INITIAL_STATE) {
			return state.colors.custom;
		},
		getDefaultTypeStyles(state: typeof INITIAL_STATE) {
			return state.typographies.default;
		},
		getCustomTypeStyles(state: typeof INITIAL_STATE) {
			return state.typographies.custom;
		},
		getButtonStyles(state: typeof INITIAL_STATE) {
			return state.buttons;
		},
	},
});

register(stylesStore);
